import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import api from '../api';

const ProdutoSemControleForm = ({ produto, fecharModal, recarregar }) => {
  const [nome, setNome] = useState('');
  const [unidade, setUnidade] = useState('unidade');
  const [peso, setPeso] = useState('1');
  const [custoFixo, setCustoFixo] = useState('');
  const [salvando, setSalvando] = useState(false);

  // ✅ APENAS 4 UNIDADES
  const unidadesDisponiveis = [
    { valor: 'unidade', label: 'Unidade' },
    { valor: 'caixa', label: 'Caixa' },
    { valor: 'kg', label: 'Quilograma (kg)' },
    { valor: 'l', label: 'Litro (l)' }
  ];

  useEffect(() => {
    if (produto) {
      setNome(produto.nome);
      setUnidade(produto.unidade_peso);
      setPeso(produto.peso_embalagem?.toString() || '1');
      
      // ✅ CORREÇÃO: Formatar diretamente sem dividir por 100
      if (produto.custo_fixo) {
        const numero = parseFloat(produto.custo_fixo);
        setCustoFixo(numero.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }));
      }
    }
  }, [produto]);

  // ✅ AUTO-PREENCHER PESO AO MUDAR UNIDADE
  const handleUnidadeChange = (novaUnidade) => {
    setUnidade(novaUnidade);
    
    // Se mudou para kg ou l, auto-preencher com 1.000
    if ((novaUnidade === 'kg' || novaUnidade === 'l') && peso === '1') {
      setPeso('1.000');
    }
    // Se mudou para unidade/caixa, voltar para 1
    if ((novaUnidade === 'unidade' || novaUnidade === 'caixa')) {
      setPeso('1');
    }
  };

  const pesoHabilitado = unidade === 'kg' || unidade === 'l';

  // Função para formatar valor monetário
  const formatarMoeda = (valor) => {
    // Remove tudo que não é número
    const apenasNumeros = valor.replace(/\D/g, '');
    
    // Converte para número com 2 casas decimais
    const numero = parseFloat(apenasNumeros) / 100;
    
    // Formata como moeda brasileira
    return numero.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // ✅ FORMATAR PESO COM PONTOS DE MILHAR
  const formatarPeso = (valor) => {
    // Remove tudo que não é número ou ponto/vírgula
    const limpo = valor.replace(/[^\d.,]/g, '');
    
    // Substitui vírgula por ponto
    const comPonto = limpo.replace(',', '.');
    
    // Converte para número
    const numero = parseFloat(comPonto);
    
    if (isNaN(numero)) return '';
    
    // Formata com ponto de milhar e 3 decimais
    return numero.toLocaleString('pt-BR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  };

  // Função para converter moeda formatada em número
  const converterParaNumero = (valorFormatado) => {
    if (!valorFormatado) return null;
    return parseFloat(valorFormatado.replace(/\./g, '').replace(',', '.'));
  };

  const handleCustoChange = (e) => {
    const valor = e.target.value;
    setCustoFixo(formatarMoeda(valor));
  };

  // ✅ HANDLER PARA PESO COM FORMATAÇÃO
  const handlePesoChange = (e) => {
    const valor = e.target.value;
    setPeso(valor);
  };

  const handlePesoBlur = () => {
    if (peso && !isNaN(peso.replace(/\./g, '').replace(',', '.'))) {
      setPeso(formatarPeso(peso));
    }
  };

  const handleSalvar = async () => {
    if (!nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    try {
      setSalvando(true);

      const dados = {
        nome: nome.trim(),
        unidade_peso: unidade,
        peso_embalagem: pesoHabilitado ? converterParaNumero(peso) : 1,
        custo_fixo: converterParaNumero(custoFixo)
      };

      if (produto) {
        await api.put(`/produtos-sem-controle/${produto.id}`, dados);
        alert('Produto atualizado com sucesso!');
      } else {
        await api.post('/produtos-sem-controle', dados);
        alert('Produto cadastrado com sucesso!');
      }

      recarregar();
      fecharModal();
    } catch (err) {
      console.error('Erro ao salvar:', err);
      alert('Erro ao salvar produto');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">
              {produto ? '✏️ Editar Produto' : '➕ Novo Produto Sem Controle'}
            </h3>
            <button
              onClick={fecharModal}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              <FaTimes />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Ovo, Corante Vermelho, Essência"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unidade *
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={unidade}
                onChange={(e) => handleUnidadeChange(e.target.value)}
              >
                {unidadesDisponiveis.map(u => (
                  <option key={u.valor} value={u.valor}>{u.label}</option>
                ))}
              </select>
            </div>

            {pesoHabilitado && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Peso da Embalagem *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="1.000"
                  value={peso}
                  onChange={handlePesoChange}
                  onBlur={handlePesoBlur}
                />
                <small className="text-gray-500 text-xs">
                  {unidade === 'kg' ? 'Ex: 1.000 (1kg), 5.000 (5kg)' : 'Ex: 1.000 (1 litro), 5.000 (5 litros)'}
                </small>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custo Fixo (R$)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="0,00"
                value={custoFixo}
                onChange={handleCustoChange}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={fecharModal}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              onClick={handleSalvar}
              disabled={salvando}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
            >
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProdutoSemControleForm;